import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Input, Form, Checkbox } from "antd";
import { IoIosSearch } from "react-icons/io";
import { FilterMatchMode } from "primereact/api";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSubdivision } from "../hooks/useSubdivision";
import { HideSortIcons } from "../utils/tableStyles";

const SubdivisionManagement = () => {
  const {
    subdivisions,
    isLoading,
    createSubdivision,
    createSubdivisionLoading,
    createSubdivisionSuccess,
    deleteSubdivision,
    updateSubdivision,
    deleteSubdivisionLoading,
    updateSubdivisionLoading,
    updateSubdivisionSuccess,
    deleteSubdivisionSuccess,
    error,
  } = useSubdivision();

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedSubdivision, setSelectedSubdivision] = useState({
    homeType: "",
    fullName: "",
    shortName: "",
    rentingOptions: [],
  });

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const homeTypes = [
    { label: "Single Family Home", value: "SFH" },
    { label: "Condominium", value: "Condo" },
    { label: "Townhouse", value: "TH" },
    { label: "Villa", value: "Villa" },
    { label: "Coach Home", value: "CoachHome" },
  ];

  const rentingOptions = [
    "No renting",
    "Annual",
    "Seasonal",
    "30-day plus",
    "Weekly",
    "Daily",
    "Other",
    "Unknown",
  ];

  const subdivisionFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      homeType: selectedSubdivision.homeType ?? "",
      fullName: selectedSubdivision.fullName ?? "",
      shortName: selectedSubdivision.shortName ?? "",
      rentingOptions: selectedSubdivision.rentingOptions ?? [],
    },
    validationSchema: yup.object({
      homeType: yup.string().required("Home type is required"),
      fullName: yup.string().required("Full name is required"),
      shortName: yup.string().required("Short name is required"),
      rentingOptions: yup.array().min(1, "Select at least one renting option"),
    }),
    onSubmit: (values) => {
      if (editingId != null) {
        updateSubdivision({
          id: editingId,
          subdivisionData: values,
        });
      } else {
        createSubdivision(values);
      }
    },
  });

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => (
    <div className="flex items-end justify-end">
      <Input
        placeholder="Search"
        className="text-sm focus:ring-0 focus:outline-none"
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        prefix={<IoIosSearch />}
      />
    </div>
  );

  const deleteMember = (id) => {
    setDeletingId(id);
    deleteSubdivision(id);
  };

  const openDialog = (subdivision = null) => {
    if (subdivision) {
      setEditingId(subdivision.id);
      setSelectedSubdivision(subdivision);
    } else {
      setEditingId(null);
      setSelectedSubdivision({
        homeType: "",
        fullName: "",
        shortName: "",
        rentingOptions: [],
      });
    }
    setVisible(true);
  };

  const handleClose = () => {
    setVisible(false);
    setSelectedSubdivision({
      homeType: "",
      fullName: "",
      shortName: "",
      rentingOptions: [],
    });
    subdivisionFormik.resetForm();
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-3">
        <Button
          icon="pi pi-pencil"
          className="p-button-text text-blue-600 hover:text-blue-800"
          onClick={() => openDialog(rowData)}
        />
        <Button
          icon={
            deletingId === rowData.id ? "pi pi-spin pi-spinner" : "pi pi-trash"
          }
          className="p-button-text text-red-600 hover:text-red-800"
          onClick={() => deleteMember(rowData.id)}
        />
      </div>
    );
  };

  const homeTypeBodyTemplate = (rowData) => {
    const homeTypeLabel =
      homeTypes.find((ht) => ht.value === rowData.homeType)?.label ||
      rowData.homeType;
    return <span>{homeTypeLabel}</span>;
  };

  const rentingOptionsBodyTemplate = (rowData) => {
    const options = Array.isArray(rowData.rentingOptions)
      ? rowData.rentingOptions
      : JSON.parse(rowData.rentingOptions || "[]");
    return <span className="text-xs">{options.join(", ")}</span>;
  };

  useEffect(() => {
    if (
      createSubdivisionSuccess ||
      updateSubdivisionSuccess ||
      deleteSubdivisionSuccess
    ) {
      handleClose();
    }
  }, [
    createSubdivisionSuccess,
    updateSubdivisionSuccess,
    deleteSubdivisionSuccess,
  ]);

  if (isLoading)
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="p-5">
      <HideSortIcons />
      <div className="flex justify-between">
        <h1 className="lg:text-2xl font-semibold text-[#003049] mb-5">
          Subdivision Management
        </h1>
        <div>
          <Button
            label="Add Subdivision"
            icon="pi pi-plus"
            className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
            onClick={() => openDialog()}
          />
        </div>
      </div>

      <div className="bg-white rounded-md mt-5 text-[18px]">
        <div className="card">
          <DataTable
            header={renderHeader}
            globalFilter={filters.global.value}
            emptyMessage="No subdivisions found."
            filters={filters}
            value={subdivisions}
            paginator
            rows={10}
            sortMode="single"
            showSortIcon={false}
            tableStyle={{ minWidth: "50rem", fontSize: "14px" }}
            className="text-xs sm:text-sm md:text-base"
          >
            <Column
              field="homeType"
              header="Home Type"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "20%" }}
              body={homeTypeBodyTemplate}
              sortable
            />
            <Column
              field="fullName"
              header="Full Name"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "25%" }}
              sortable
            />
            <Column
              field="shortName"
              header="Short Name"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "15%" }}
              sortable
            />
            <Column
              field="rentingOptions"
              header="Renting Options"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "30%" }}
              body={rentingOptionsBodyTemplate}
            />
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "10%" }}
              body={actionBodyTemplate}
            />
          </DataTable>
        </div>
      </div>

      <Dialog
        header={editingId ? "Edit Subdivision" : "Add Subdivision"}
        visible={visible}
        className="w-full lg:w-[50%]"
        onHide={handleClose}
      >
        <Form layout="vertical" onFinish={subdivisionFormik.handleSubmit}>
          <Form.Item
            label={
              <span>
                Home type <span className="text-red-500 ml-1">*</span>
              </span>
            }
            validateStatus={
              subdivisionFormik.errors.homeType &&
              subdivisionFormik.touched.homeType
                ? "error"
                : ""
            }
            help={
              subdivisionFormik.touched.homeType &&
              subdivisionFormik.errors.homeType
            }
          >
            <select
              name="homeType"
              value={subdivisionFormik.values.homeType || ""}
              onChange={(e) =>
                subdivisionFormik.setFieldValue("homeType", e.target.value)
              }
              onBlur={() => subdivisionFormik.setFieldTouched("homeType", true)}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
            >
              <option value="">Select home type</option>
              {homeTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </Form.Item>

          <Form.Item
            label={
              <span>
                Full name <span className="text-red-500 ml-1">*</span>
              </span>
            }
            validateStatus={
              subdivisionFormik.errors.fullName &&
              subdivisionFormik.touched.fullName
                ? "error"
                : ""
            }
            help={
              subdivisionFormik.touched.fullName &&
              subdivisionFormik.errors.fullName
            }
          >
            <Input
              name="fullName"
              value={subdivisionFormik.values.fullName}
              onChange={subdivisionFormik.handleChange}
              onBlur={subdivisionFormik.handleBlur}
              className="rounded-md"
              placeholder="e.g., Lakeside Estate"
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Short name <span className="text-red-500 ml-1">*</span>
              </span>
            }
            validateStatus={
              subdivisionFormik.errors.shortName &&
              subdivisionFormik.touched.shortName
                ? "error"
                : ""
            }
            help={
              subdivisionFormik.touched.shortName &&
              subdivisionFormik.errors.shortName
            }
          >
            <Input
              name="shortName"
              value={subdivisionFormik.values.shortName}
              onChange={subdivisionFormik.handleChange}
              onBlur={subdivisionFormik.handleBlur}
              className="rounded-md"
              placeholder="e.g., LKS"
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Renting options <span className="text-red-500 ml-1">*</span>
              </span>
            }
            validateStatus={
              subdivisionFormik.errors.rentingOptions &&
              subdivisionFormik.touched.rentingOptions
                ? "error"
                : ""
            }
            help={
              subdivisionFormik.touched.rentingOptions &&
              subdivisionFormik.errors.rentingOptions
            }
          >
            <Checkbox.Group
              value={subdivisionFormik.values.rentingOptions}
              onChange={(values) =>
                subdivisionFormik.setFieldValue("rentingOptions", values)
              }
              className="flex flex-col gap-2"
            >
              {rentingOptions.map((option) => (
                <Checkbox key={option} value={option}>
                  {option}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>

          <button
            type="submit"
            className="btn w-full bg-[#BA1500] text-white py-3"
            disabled={createSubdivisionLoading || updateSubdivisionLoading}
          >
            <i
              className={`${
                createSubdivisionLoading || updateSubdivisionLoading
                  ? "pi pi-spin pi-spinner"
                  : "pi pi-save"
              } mr-2`}
            ></i>
            {editingId != null
              ? updateSubdivisionLoading
                ? "Updating..."
                : "Update"
              : createSubdivisionLoading
              ? "Saving..."
              : "Save"}
          </button>
        </Form>
      </Dialog>
    </div>
  );
};

export default SubdivisionManagement;
